import React, { useEffect } from "react";
import {
  Stack,
  StackProps,
  InputLabel,
  Divider,
  Link,
  Typography,
  Chip,
  Switch,
  styled,
  FormControlLabel,
  Alert,
} from "@mui/material";
import FormSection from "../../../Forms/FormSection/form-section";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import DataTable, { CellSize } from "../../../Table/table";
import ConfigFormIcon from "../../ConfigFormIcon";
import ChatOutlinedIcon from "@mui/icons-material/ChatOutlined";

import { DbOrderBasketItemInfos } from "../../../../Core/Api/Types/db-order-basket-item-infos";
import { DbOrderManualTaskTemplateInfos } from "../../../../Core/Api/Types/db-order-manual-task-template-infos";
import FormAutocomplete from "../../../Forms/FormAutocomplete/form-autocomplete";
import {
  ItemField,
  ProductField,
  ReduxUpdateItemManualTaskField,
  SettingRootField,
  UpdateItemManualTaskFormFieldOnBasket,
  UpdateItemManualTaskOnBasket,
  UpdateItemProductRootOnBasket,
  UpdateItemRootOnBasket,
  UpdateItemSettingsRootOnBasket,
} from "../../../../Redux/Reducers/redux-slice-basket";
import { useDispatch } from "react-redux";
import { useAppSelector } from "../../../../Redux/redux-store";
import { GetSectionIsValid } from "../../../../Redux/Reducers/redux-slice-form-validation";
import { DbOrderManualTaskTasksInfos } from "../../../../Core/Api/Types/db-order-manual-task-tasks-infos";
import { planningUserTaskService } from "../../../../Core/Api/services";
import { GetManualTasksTemplateWithComputedPricesRequest } from "../../../../Core/Api/Types/get-manual-tasks-template-with-computed-prices-request";
import { ManualTasksTemplateDisabledTasks } from "../../../../Core/Api/Types/manual-tasks-template-disabled-tasks";
import { OrderManualTaskTasksPriceRule } from "../../../../Core/Api/Types/order-manual-task-tasks-price-rule";
import { CurrencyPriceInfos } from "../../../../Core/Api/Types/currency-price-infos";
import { DbOrderManualTaskTemplateFormFieldInfos } from "../../../../Core/Api/Types/db-order-manual-task-template-form-field-infos";
import { MetadataFieldBaseTypeEnum } from "../../../../Core/Api/Types/metadata-field-base-type-enum";
import { MetadataFieldValueCardinality } from "../../../../Core/Api/Types/metadata-field-value-cardinality";
import FormInputText from "../../../Forms/FormInputText/form-input-text";
import { GridColDef } from "@mui/x-data-grid-premium";
import { AclMatch, UserHasAcls } from "../../../../Utils/helpers";
import { AclTypes } from "../../../../Core/Api/Types/acl-types";
import FormInputNumber from "../../../Forms/FormInputNumber/form-input-number";
import dayjs from "dayjs";
import FormInputDateTime from "../../../Forms/FormInputDate/form-input-date-time";
import GlobalConst from "../../../../Core/Utils/Constants/globals";
import { useTranslation } from "react-i18next";
import { immerable } from "immer";
interface ConfigManualTaskFormProps extends StackProps {
  item: DbOrderBasketItemInfos;
  formid: string;
  defaultExpended: boolean;
  hideAlert?: boolean;
}

const ConfigManualTaskForm: React.FC<ConfigManualTaskFormProps> = (props) => {
  const { t } = useTranslation();
  const { formid, item, defaultExpended, hideAlert = false, ...otherProps } = props;

  // Templates are now stores in the Manual Tasks profile settings
  const templates: DbOrderManualTaskTemplateInfos[] = item.selectedProduct?.settings.templates;

  const dispatch = useDispatch();

  // Update product root base object into redux
  const handleUpdateItem = (field: ItemField, value: any) => {
    dispatch(UpdateItemRootOnBasket({ itemId: item.id, field: field, value: value }));
  };

  // Update settings obj
  const handleUpdateSettings = (field: SettingRootField, value: any) => {
    dispatch(UpdateItemSettingsRootOnBasket({ itemId: item.id, field: field, value: value }));
  };

  const handleFormField = (fieldIndex: number, value: any) => {
    dispatch(UpdateItemManualTaskFormFieldOnBasket({ itemId: item.id, fieldIndex: fieldIndex, value: value }));
  };
  // Turn on/off task
  const switchTaskOption = (jobIndex: number, taskIndex: number, task: DbOrderManualTaskTasksInfos) => {
    let newValue = !task.isDisabled;

    dispatch(
      UpdateItemManualTaskOnBasket({
        itemId: item.id,
        jobIndex: jobIndex,
        taskIndex: taskIndex,
        field: ReduxUpdateItemManualTaskField.Disabled,
        value: newValue,
      })
    );

    updatePricesWithBackend(jobIndex, taskIndex, newValue);
  };

  // Form validator
  const formSectionManualTask = useAppSelector(GetSectionIsValid(formid, "sectionManualTask"));
  const formSectionManualTaskFields = useAppSelector(GetSectionIsValid(formid, "sectionManualTaskFields"));

  // Price rule management
  const updatePricesWithBackend = (jobIndex: number, taskIndex: number, newValue: boolean) => {
    if (!item.manualTasksTemplate) return;

    let priceDeps: OrderManualTaskTasksPriceRule[] = [];

    let manualTaskDisabled: ManualTasksTemplateDisabledTasks[] = [];

    if (item.manualTasksTemplate) {
      item.manualTasksTemplate.jobs?.forEach((job, jIndex) => {
        let disabled: ManualTasksTemplateDisabledTasks = {
          jobId: job.id,
          taskIndexes: [],
        };

        job.tasks?.forEach((task, index) => {
          if (jIndex === jobIndex && index === taskIndex) {
            if (newValue) disabled.taskIndexes!.push(index);
          } else {
            if (task.isDisabled && task.canBeDisabled) disabled.taskIndexes!.push(index);
          }
        });

        if (disabled.taskIndexes!.length > 0) manualTaskDisabled.push(disabled);
      });
    }

    let request: GetManualTasksTemplateWithComputedPricesRequest = {
      templateId: item.manualTasksTemplate!.id,
      priceRulesParameters: priceDeps,
      disabledTaskIndexesGroupedByJobIds: manualTaskDisabled,
    };
    planningUserTaskService.GetManualTasksTemplateByIdWithComputedPrices(request).then((response) => {
      // Keep user data and do not override
      if (response!.template!.formFields && item?.manualTasksTemplate?.formFields)
        response!.template!.formFields! = item?.manualTasksTemplate?.formFields!;

      dispatch(
        UpdateItemRootOnBasket({ itemId: item.id, field: ItemField.ManualTaskTemplate, value: response.template })
      );
    });
  };

  // Price totals sum
  const pricesTotals = React.useMemo((): CurrencyPriceInfos[] => {
    let prices: CurrencyPriceInfos[] = [];

    if (item.manualTasksTemplate) {
      item.manualTasksTemplate.jobs?.forEach((job, jIndex) => {
        job.tasks?.forEach((task, index) => {
          if (task.prices) {
            task.prices.forEach((taskPrice) => {
              let priceMatch = prices.find((x) => x.currency === taskPrice.currency);
              if (!priceMatch)
                prices.push({
                  currency: taskPrice.currency,
                  price: taskPrice.price,
                  priceDetails: [],
                  [immerable]: true,
                });
              else priceMatch.price! += taskPrice.price!;
            });
          }
        });
      });
    }

    if (prices.length > 0) {
      dispatch(UpdateItemRootOnBasket({ itemId: item.id, field: ItemField.Price, value: prices[0] }));
      dispatch(UpdateItemRootOnBasket({ itemId: item.id, field: ItemField.PriceOverride, value: null }));
      dispatch(UpdateItemProductRootOnBasket({ itemId: item.id, field: ProductField.Price, value: prices[0] }));
      console.log("basket: item " + item.id + " new price = " + prices[0].price);
    }

    return prices;
  }, [item.manualTasksTemplate]);

  const getJobColumns = (headTitle: string, jobIndex: number): GridColDef[] => {
    return [
      {
        field: "name",
        headerName: headTitle,
        flex: CellSize.small,
        minWidth: CellSize.small,
        renderCell: (data) => (
          <Stack sx={{ opacity: data?.row?.isDisabled ? 0.3 : 1 }}>
            <Typography variant="body2">{data.value}</Typography>
            <Typography variant="body2" color="text.tier">
              {data?.row?.description}
            </Typography>
          </Stack>
        ),
      },
      {
        field: "isDisabled",
        headerName: t("common:option"),
        flex: CellSize.small,
        minWidth: CellSize.small,
        renderCell: (data) => (
          <>
            {!data?.row?.canBeDisabled ? (
              <Chip label={t("common:mandatory")} color="info" size="small" />
            ) : (
              <Switch
                checked={!data.value}
                onChange={() => {
                  switchTaskOption(jobIndex, data.api.getRowIndexRelativeToVisibleRows(data.id), data.row);
                }}
              />
            )}
          </>
        ),
      },
      {
        field: "prices",
        headerName: t("common:prices"),
        flex: CellSize.small,
        minWidth: CellSize.small,
        renderCell: (data) => (
          <Stack direction="row" alignItems="center" sx={{ opacity: data?.row?.isDisabled ? 0.3 : 1 }}>
            {data.value.map((price, index) => (
              <Typography
                variant="body1"
                color="text.primary"
                component="span"
                ml={index !== data.value.length - 1 ? "" : 0.5}
                key={index}
              >
                {price.price}
                <Typography variant="body2" color="text.tier" component="span">
                  &nbsp;{price.currency} {index !== data.value.length - 1 ? "/" : ""}
                </Typography>
              </Typography>
            ))}
            {(!data.value || data.value.length === 0) && (
              <Typography variant="body2" color="text.tier" component="span">
                &nbsp;{t("common:notDefined")}
              </Typography>
            )}
          </Stack>
        ),
      },
    ];
  };

  const _renderField = (field: DbOrderManualTaskTemplateFormFieldInfos, fieldIndex: number) => {
    switch (field.baseType) {
      case MetadataFieldBaseTypeEnum.String:
        if (
          (field.predefinedValues === null || field.predefinedValues.length === 0) &&
          field.valuesCardinality === MetadataFieldValueCardinality.One
        )
          return (
            <FormInputText
              label={field.label!}
              startAdornment={<ChatOutlinedIcon />}
              placeholder={field.label!}
              value={field.values ? field.values[0] : ""}
              onChange={(v) => handleFormField(fieldIndex, [v.target.value])}
              validationProps={{
                formId: formid,
                formSectionId: "sectionManualTaskFields",
                fieldId: "formfield-" + fieldIndex,
                initialState: false,
                required: field.isMandatory,
              }}
            />
          );
        else
          return (
            <FormAutocomplete
              label={field.label!}
              key={`${formid}-mt-template`}
              options={
                field.predefinedValues !== null && field.predefinedValues.length > 0 ? field.predefinedValues : []
              }
              multiple={field.valuesCardinality === MetadataFieldValueCardinality.N}
              freeSolo={field.predefinedValues === null || field.predefinedValues.length === 0}
              onChange={(v) => {
                let value = field.valuesCardinality === MetadataFieldValueCardinality.N ? v : [v];
                handleFormField(fieldIndex, value);
              }}
              onReset={(v) => handleFormField(fieldIndex, [])}
              value={
                field.values
                  ? field.valuesCardinality === MetadataFieldValueCardinality.N
                    ? field.values
                    : field.values[0]
                  : undefined
              }
              popupIcon={<ExpandMoreRoundedIcon />}
              getOptionLabel={(value: any) => {
                return value;
              }}
              validationProps={{
                formId: formid,
                formSectionId: "sectionManualTaskFields",
                fieldId: "formfield-" + fieldIndex,
                required: field.isMandatory,
                initialState: false,
              }}
              placeholder={
                field.valuesCardinality === MetadataFieldValueCardinality.N
                  ? t("field:placeholder.typeValueAndPressEnter")
                  : t("field:placeholder.selectValue")
              }
            />
          );
      case MetadataFieldBaseTypeEnum.Long:
      case MetadataFieldBaseTypeEnum.Integer:
        return (
          <FormInputNumber
            label={field.label!}
            startAdornment={<ChatOutlinedIcon />}
            placeholder={field.label!}
            value={field.values ? field.values[0] : undefined}
            onChange={(v) => handleFormField(fieldIndex, [v.target.value])}
            validationProps={{
              formId: formid,
              formSectionId: "sectionManualTaskFields",
              fieldId: "formfield-" + fieldIndex,
              initialState: false,
              required: field.isMandatory,
            }}
            stackProps={{}}
          />
        );
      case MetadataFieldBaseTypeEnum.Boolean:
        return (
          <Typography color="text.tier">
            <FormControlLabel
              sx={{ mt: 0, ml: 0, mb: 2 }}
              control={
                <Switch
                  size="small"
                  checked={field.values ? field.values?.includes("True") : false}
                  value={field.values ? field.values?.includes("True") : false}
                  onChange={(e, c) => handleFormField(fieldIndex, [c ? "True" : "False"])}
                />
              }
              label={field.label}
            />
          </Typography>
        );
      case MetadataFieldBaseTypeEnum.DateTime:
        return (
          <FormInputDateTime
            label={field.label!}
            format={GlobalConst.MOMENT_DATE_FORMAT_FULL}
            value={field.values && field.values.length > 0 ? dayjs(field.values[0]) : undefined}
            onChange={(value) => handleFormField(fieldIndex, [value])}
            onReset={() => handleFormField(fieldIndex, null)}
            stackProps={{ mt: 1 }}
            validationProps={{
              formId: formid,
              formSectionId: "sectionManualTaskFields",
              fieldId: "formfield-" + fieldIndex,
              initialState: false,
              required: field.isMandatory,
              onlyInFuture: true,
            }}
          />
        );
    }

    return <></>;
  };

  useEffect(() => {
    item?.manualTasksTemplate?.formFields?.forEach((field, index) => {
      if (field.baseType === MetadataFieldBaseTypeEnum.Boolean && (!field.values || field.values.length === 0))
        handleFormField(index, ["False"]);
    });
  }, [item?.manualTasksTemplate?.formFields]);

  // If no template matches, hide all this
  if (templates?.filter((x) => x.productType === item?.selectedProduct?.type).length === 0) return <></>;

  return (
    <>
      {!hideAlert && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography>
            {t("alert:info.availabilityToAssociateManualTasksToConfig")}
            <br />
            {t("alert:info.actionsCanBeCharged")}
          </Typography>
        </Alert>
      )}
      <StyledForm>
        <Stack spacing={1.5} width={"100%"} {...otherProps}>
          <FormSection
            icon={<ConfigFormIcon icon="import" />}
            title={t("form:section.title.templateAndJobs")}
            status={formSectionManualTask ? "complete" : "todo"}
            statusText={formSectionManualTask ? t("common:configured") : t("common:configurationRequired")}
            defaultExpanded={defaultExpended}
          >
            <Stack spacing={3}>
              <Stack flex={1}>
                <InputLabel sx={{ mb: 1 }}>{t("field:label.availableTemplates")}</InputLabel>
                <FormAutocomplete
                  id={`${formid}-mt-template`}
                  options={templates?.filter((x) => x.productType === item?.selectedProduct?.type).map((tpl) => tpl.id)}
                  onChange={(v) => {
                    handleUpdateItem(ItemField.ManualTaskTemplateId, v);
                    handleUpdateItem(
                      ItemField.ManualTaskTemplate,
                      templates.find((x) => x.id === v)
                    );
                  }}
                  onReset={(v) => {
                    handleUpdateItem(ItemField.ManualTaskTemplateId, null);
                    handleUpdateItem(ItemField.ManualTaskTemplate, null);
                  }}
                  value={item.manualTasksTemplateId}
                  popupIcon={<ExpandMoreRoundedIcon />}
                  getOptionLabel={(value: any) => {
                    let match = templates?.find((x) => x.id === value);
                    if (match) return match.description!;
                    else return "";
                  }}
                  isOptionEqualToValue={(option, value) => option === value}
                  filterOptions={(x, s) => {
                    let match = templates?.filter((x) =>
                      x.description!.toLowerCase().includes(s.inputValue.toLowerCase())
                    );
                    return x.filter((a) => match.map((s) => s.id).includes(a));
                  }}
                  validationProps={{
                    formId: formid,
                    formSectionId: "sectionManualTask",
                    fieldId: "templateId",
                    required: true,
                    initialState: false,
                  }}
                />
              </Stack>
              {item?.manualTasksTemplateId && (
                <Stack>
                  <Divider sx={{ mb: 3 }} />
                  <Stack direction="row" justifyContent="space-between" mb={1.5}>
                    <Typography color="text.secondary" fontWeight={500}>
                      {t("component:body.templateDetails")}
                    </Typography>
                    <Link
                      component="button"
                      variant="button"
                      color="error"
                      underline="hover"
                      onClick={() => {
                        handleUpdateItem(ItemField.ManualTaskTemplateId, null);
                        handleUpdateItem(ItemField.ManualTaskTemplate, null);
                      }}
                    >
                      {t("button:unselectTemplate")}
                    </Link>
                  </Stack>

                  {item.manualTasksTemplate?.jobs?.map((job, index) => (
                    <DataTable
                      noControls
                      rows={job.tasks!}
                      columns={getJobColumns(`Job #${index + 1} - ${job.name!}`, index)}
                      rowHeight={44}
                      columnHeaderHeight={32}
                      hideFooter={true}
                      key={`dt${index}`}
                      sx={{ mb: 1 }}
                    />
                  ))}

                  <Divider sx={{ mb: 3, mt: 2 }} />

                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="subtitle2" fontWeight={600}>
                      {t("common:total")}
                      <Typography component="span" variant="body1" color="text.tier">
                        &nbsp; (
                        {item?.manualTasksTemplate?.isTreatedExternally
                          ? t("component:configManualTaskFormRender.body.performedByThirdPart")
                          : t("component:configManualTaskFormRender.body.performedByVdmTeams")}
                        )
                      </Typography>
                    </Typography>
                    <Stack direction="row" alignItems="center">
                      {pricesTotals.map((price, index) => (
                        <Typography
                          variant="subtitle2"
                          component="span"
                          fontWeight={600}
                          ml={0.5}
                          key={`totalp${index}`}
                        >
                          {price.price}
                          <Typography variant="subtitle2" component="span" fontWeight={600}>
                            {" "}
                            {price.currency} {index != pricesTotals.length - 1 ? "/" : ""}
                          </Typography>
                        </Typography>
                      ))}
                      {pricesTotals.length === 0 && (
                        <Typography variant="subtitle2" component="span" fontWeight={600}>
                          {t("common:notDefined")}
                        </Typography>
                      )}
                    </Stack>
                  </Stack>
                </Stack>
              )}
            </Stack>
          </FormSection>

          {item?.manualTasksTemplate?.formFields && item?.manualTasksTemplate?.formFields.length > 0 && (
            <FormSection
              icon={<ConfigFormIcon icon="meta" />}
              title={t("form:section.title.metadataFormFields")}
              status={formSectionManualTaskFields ? "complete" : "warning"}
              statusText={formSectionManualTaskFields ? t("common:configured") : t("common:configurationRequired")}
            >
              {item?.manualTasksTemplate?.formFields && item?.manualTasksTemplate?.formFields.length > 0 && (
                <>{item?.manualTasksTemplate?.formFields.map((field, index) => _renderField(field, index))}</>
              )}
              {/*<Divider sx={{ mt: 3, mb: 3 }} />
            <FormControl sx={{ mb: 2 }}>
              <InputLabel shrink htmlFor="comment" sx={{ display: "inline-flex" }}>
                Add a comment <Typography color="text.tier"> &nbsp; (Optional)</Typography>
              </InputLabel>
              <OutlinedInput defaultValue="" id={`${formid}comment`} multiline rows={5} />
            </FormControl>*/}
            </FormSection>
          )}

          {UserHasAcls([AclTypes.aclAdmin, AclTypes.aclSuperUser], AclMatch.OneAtLeast) && (
            <FormSection
              icon={<ConfigFormIcon icon="meta" />}
              title={t("form:section.title.quoteWorkflow")}
              defaultExpanded
            >
              <FormControlLabel
                sx={{ mt: 0, ml: 0, mb: 0 }}
                control={
                  <Switch
                    size="small"
                    checked={item.selectedProduct?.settings.quote !== null}
                    value={item.selectedProduct?.settings.quote !== null}
                    disabled={item.itemReferenceId !== null}
                    onChange={(e, c) => {
                      if (c)
                        handleUpdateSettings(SettingRootField.ManualTaskQuote, {
                          initiator: "Proposal",
                          templateType: "Generic",
                        });
                      else handleUpdateSettings(SettingRootField.ManualTaskQuote, null);
                    }}
                  />
                }
                label={t("field:label.enableQuoteProposal")}
              />
            </FormSection>
          )}
        </Stack>
      </StyledForm>
    </>
  );
};

export default ConfigManualTaskForm;

const StyledForm = styled("form")(({ theme }) => ({}));
